// ============================================
// FILE: src/utils/imageHelpers.js
// ============================================

/**
 * Shared utility function to get product image with fallback to emoji
 * Used across HomeScreen, ProductDetailsModal, OrdersScreen, and OrderConfirmationModal
 */
export const getProductImage = (product, size = 'medium') => {
  // Check for featured_image (backend) or image field
  const imageUrl = product?.featured_image || product?.image;
  
  // If product has a valid image URL, return img element
  if (imageUrl && typeof imageUrl === 'string' && !imageUrl.includes('�') && imageUrl !== 'null') {
    const fullImageUrl = imageUrl.startsWith('/') ? `http://localhost:8000${imageUrl}` : imageUrl;
    
    // Define size classes
    const sizeClasses = {
      small: 'w-8 h-8',
      medium: 'w-12 h-12', 
      large: 'w-16 h-16',
      xl: 'w-20 h-20',
      modal: 'w-full h-64'
    };
    
    if (size === 'modal') {
      return (
        <div className="relative w-full h-64">
          <img 
            src={fullImageUrl}
            alt={product?.name || 'Product'}
            className="w-full h-64 object-cover rounded-xl"
            onError={(e) => {
              // Fallback to emoji if image fails to load
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div className="w-full h-64 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center" style={{display: 'none'}}>
            <span className="text-8xl">{getProductEmoji(product, 'xl')}</span>
          </div>
        </div>
      );
    }
    
    return (
      <img 
        src={fullImageUrl}
        alt={product?.name || 'Product'}
        className={`${sizeClasses[size]} object-cover rounded-lg bg-gray-100`}
        onError={(e) => {
          // Fallback to emoji if image fails to load
          e.target.style.display = 'none';
          if (e.target.nextSibling) {
            e.target.nextSibling.style.display = 'flex';
          }
        }}
      />
    );
  }
  
  // Return emoji directly if no image URL
  return getProductEmoji(product, size);
};

/**
 * Get product emoji with size styling
 */
export const getProductEmoji = (product, size = 'medium') => {
  const sizeClasses = {
    small: 'w-8 h-8 text-lg',
    medium: 'w-12 h-12 text-2xl', 
    large: 'w-16 h-16 text-3xl',
    xl: 'w-20 h-20 text-4xl',
    modal: 'w-full h-64 text-8xl'
  };
  
  // Determine emoji based on product name or default fallback
  let emoji = '🥛'; // Default milk emoji
  
  if (product?.name || product?.product_name) {
    const name = (product.name || product.product_name).toLowerCase();
    
    if (name.includes('milk')) {
      emoji = '🥛';
    } else if (name.includes('butter') || name.includes('cheese')) {
      emoji = '🧈';
    } else if (name.includes('yogurt') || name.includes('curd')) {
      emoji = '🍯';
    } else if (name.includes('cream')) {
      emoji = '🥛';
    } else if (name.includes('lassi')) {
      emoji = '🥤';
    }
  }
  
  const containerClass = size === 'modal' ? 'w-full h-64 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center' : `${sizeClasses[size]} flex items-center justify-center bg-gray-100 rounded-lg`;
  
  return (
    <div className={containerClass}>
      <span>{emoji}</span>
    </div>
  );
};

/**
 * Get product image for order items (handles different data structures)
 */
export const getOrderItemImage = (item, size = 'medium') => {
  // Try to construct a product object from order item
  const product = {
    name: item.name || item.product_name || item.product?.name,
    featured_image: item.featured_image || item.product?.featured_image || item.image || item.product?.image,
    image: item.image || item.product?.image
  };
  
  return getProductImage(product, size);
};